import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BlobField from '../components/BlobField';
import Toast from '../components/Toast';
import api, { formatApiErrorDetail } from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Schedule Launch — creator composer. Phase 4.
 * Requires an existing Clan (400 back from the API if none).
 */

// Default: 24h from now, rounded to next 15-min slot
function defaultDateTime() {
  const d = new Date(Date.now() + 24 * 3600 * 1000);
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export default function ScheduleLaunch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clan, setClan] = useState(undefined);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [when, setWhen] = useState(defaultDateTime());
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.get('/clans/mine').then(({ data }) => setClan(data.clan)).catch(() => setClan(null));
  }, []);

  const canSubmit = useMemo(
    () => title.trim().length >= 3 && when && !submitting,
    [title, when, submitting]
  );

  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, '').slice(0, 24);
    if (!t || tags.includes(t)) return;
    if (tags.length >= 5) return;
    setTags([...tags, t]);
    setTagInput('');
  };
  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErr('');
    try {
      // Local datetime-local → ISO with local tz offset
      const startIso = new Date(when).toISOString();
      const { data } = await api.post('/launches', {
        title: title.trim(),
        description: description.trim(),
        start_at: startIso,
        duration_minutes: 60, // legacy field — no longer surfaced in UI; sensible default
        tags,
      });
      setToast({ msg: '✓ Launch scheduled' });
      setTimeout(() => navigate(`/launch/${data.launch.id}`), 700);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not schedule.');
      setSubmitting(false);
    }
  };

  if (clan === undefined) {
    return (<><BlobField /><div className="loading-shell">loading…</div></>);
  }

  if (clan === null) {
    return (
      <>
        <BlobField />
        <div className="sl-wrap" data-testid="sl-no-clan">
          <div className="sl-card" style={{ textAlign: 'center' }}>
            <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 20, marginBottom: 8 }}>Start a Clan first</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>Launches are hosted by a Clan — spin one up in a few clicks.</p>
            <button className="cf-primary-btn" onClick={() => navigate('/create-clan')} data-testid="sl-goto-create-clan">
              Start my Clan
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!user?.verified) {
    return (
      <>
        <BlobField />
        <div className="sl-wrap" data-testid="sl-not-verified">
          <div className="sl-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 42, marginBottom: 8 }}>🔒</div>
            <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 22, marginBottom: 8 }}>
              Only verified creators can schedule launches
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
              Launches are high-visibility events — we require the ✓ verified badge so your Clan knows
              they're joining the real you. It's free and takes minutes.
            </p>
            <button className="cf-primary-btn" onClick={() => navigate('/verify')} data-testid="sl-goto-verify">
              Get verified →
            </button>
            <div>
              <button
                type="button"
                onClick={() => navigate('/launches')}
                style={{ marginTop: 12, background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}
                data-testid="sl-back-to-launches"
              >
                ← Back to launches
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <BlobField />
      <div className="sl-wrap" data-testid="schedule-launch">
        <div className="sl-topbar">
          <button className="cf-icon-btn" onClick={() => navigate('/launches')} aria-label="Back" data-testid="sl-back">
            <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
              <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className="sl-title">Schedule a Launch</h1>
        </div>

        <div className="sl-card">
          <div className="sl-field">
            <label className="sl-label">Title *</label>
            <input
              className="sl-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 120))}
              placeholder="Your Grand Launch"
              data-testid="sl-title-input"
            />
          </div>

          <div className="sl-field">
            <label className="sl-label">Description</label>
            <textarea
              className="sl-input"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 1000))}
              placeholder="What will happen? Anything members should prep?"
              data-testid="sl-desc-input"
            />
            <div className="sl-hint">{description.length}/1000</div>
          </div>

          <div className="sl-field">
            <label className="sl-label">Starts at</label>
            <input
              className="sl-input"
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              data-testid="sl-when-input"
            />
          </div>

          <div className="sl-field">
            <label className="sl-label">Tags <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>(up to 5)</span></label>
            <div className="sl-tag-row">
              <input
                className="sl-input"
                type="text"
                placeholder="Add a tag (e.g. live, options)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                data-testid="sl-tag-input"
              />
              <button className="sl-tag-add" onClick={addTag} disabled={!tagInput.trim() || tags.length >= 5} data-testid="sl-tag-add">Add</button>
            </div>
            {tags.length > 0 && (
              <div className="sl-tag-list">
                {tags.map((t) => (
                  <span key={t} className="sl-tag-chip">
                    #{t}
                    <button onClick={() => removeTag(t)} aria-label={`Remove ${t}`}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {err && <div className="cs-err" data-testid="sl-err">{err}</div>}

          <button
            className="cf-primary-btn"
            style={{ width: '100%' }}
            disabled={!canSubmit}
            onClick={submit}
            data-testid="sl-submit"
          >
            {submitting ? 'Scheduling…' : 'Schedule launch'}
          </button>
        </div>
      </div>

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </>
  );
}
